import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from './utils';
import { Badge } from './Badge';

export interface TagInputProps {
  /** Current selected tags */
  value: string[];
  /** Callback when tags change */
  onChange: (tags: string[]) => void;
  /** Available tags for autocomplete suggestions */
  suggestions?: string[];
  /** Loading state for suggestions */
  isLoading?: boolean;
  /** Maximum tag length (default: 100) */
  maxTagLength?: number;
  /** Maximum number of tags (optional) */
  maxTags?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  hasError?: boolean;
  /** Accessible label */
  ariaLabel?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * TagInput component for managing tags with autocomplete suggestions.
 * Supports keyboard navigation (Enter to add, Backspace to remove).
 */
export const TagInput = React.forwardRef<HTMLDivElement, TagInputProps>(
  (
    {
      value = [],
      onChange,
      suggestions = [],
      isLoading = false,
      maxTagLength = 100,
      maxTags,
      placeholder = 'Add tags...',
      disabled = false,
      hasError = false,
      ariaLabel = 'Tag input',
      className,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState('');
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [focusedSuggestionIndex, setFocusedSuggestionIndex] = React.useState(-1);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Filter suggestions based on input and exclude already selected tags
    const filteredSuggestions = React.useMemo(() => {
      if (!inputValue.trim()) return [];
      const lowercaseInput = inputValue.toLowerCase();
      return suggestions
        .filter(
          (tag) =>
            tag.toLowerCase().includes(lowercaseInput) && !value.includes(tag)
        )
        .slice(0, 8);
    }, [inputValue, suggestions, value]);

    // Add a tag
    const addTag = React.useCallback(
      (tag: string) => {
        const trimmedTag = tag.trim();
        if (!trimmedTag) return;
        if (trimmedTag.length > maxTagLength) return;
        if (value.includes(trimmedTag)) return;
        if (maxTags && value.length >= maxTags) return;

        onChange([...value, trimmedTag]);
        setInputValue('');
        setShowSuggestions(false);
        setFocusedSuggestionIndex(-1);
      },
      [value, onChange, maxTagLength, maxTags]
    );

    // Remove a tag
    const removeTag = React.useCallback(
      (tagToRemove: string) => {
        onChange(value.filter((tag) => tag !== tagToRemove));
      },
      [value, onChange]
    );

    // Handle keyboard events
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (focusedSuggestionIndex >= 0 && filteredSuggestions[focusedSuggestionIndex]) {
            addTag(filteredSuggestions[focusedSuggestionIndex]);
          } else if (inputValue.trim()) {
            addTag(inputValue);
          }
        } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
          removeTag(value[value.length - 1]);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setFocusedSuggestionIndex((prev) =>
            prev < filteredSuggestions.length - 1 ? prev + 1 : prev
          );
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setFocusedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === 'Escape') {
          setShowSuggestions(false);
          setFocusedSuggestionIndex(-1);
        }
      },
      [inputValue, value, filteredSuggestions, focusedSuggestionIndex, addTag, removeTag]
    );

    // Handle input change
    const handleInputChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        if (newValue.length <= maxTagLength) {
          setInputValue(newValue);
          setShowSuggestions(true);
          setFocusedSuggestionIndex(-1);
        }
      },
      [maxTagLength]
    );

    // Handle click outside to close suggestions
    React.useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setShowSuggestions(false);
          setFocusedSuggestionIndex(-1);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const canAddMore = !maxTags || value.length < maxTags;

    return (
      <div ref={ref} className={cn('relative', className)}>
        <div
          ref={containerRef}
          className={cn(
            'flex min-h-10 w-full flex-wrap gap-1.5 rounded-lg border bg-white px-3 py-2',
            'focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2',
            hasError && 'border-red-500',
            disabled && 'cursor-not-allowed opacity-50',
            !hasError && 'border-gray-300'
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {/* Render tags */}
          {value.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              <span className="max-w-[150px] truncate">{tag}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                disabled={disabled}
                className={cn(
                  'ml-1 rounded-full p-0.5 hover:bg-gray-300',
                  'focus:outline-none focus:ring-1 focus:ring-primary',
                  disabled && 'pointer-events-none'
                )}
                aria-label={`Eliminar ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}

          {/* Input field */}
          {canAddMore && (
            <div className="flex flex-1 items-center gap-1">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowSuggestions(true)}
                placeholder={value.length === 0 ? placeholder : ''}
                disabled={disabled}
                aria-label={ariaLabel}
                aria-expanded={showSuggestions && filteredSuggestions.length > 0}
                aria-autocomplete="list"
                className={cn(
                  'flex-1 min-w-[120px] bg-transparent text-sm outline-none',
                  'placeholder:text-gray-400',
                  disabled && 'cursor-not-allowed'
                )}
              />
              {inputValue.trim() && (
                <button
                  type="button"
                  onClick={() => addTag(inputValue)}
                  disabled={disabled}
                  className={cn(
                    'shrink-0 rounded-md bg-primary px-2 py-1 text-xs font-medium text-white',
                    'hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
                    disabled && 'pointer-events-none opacity-50'
                  )}
                >
                  Agregar
                </button>
              )}
            </div>
          )}
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && !disabled && (
          <div
            className={cn(
              'absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-md',
              'max-h-[200px] overflow-auto'
            )}
            role="listbox"
          >
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Cargando...</div>
            ) : (
              filteredSuggestions.map((suggestion, index) => (
                <button
                  key={suggestion}
                  type="button"
                  role="option"
                  aria-selected={index === focusedSuggestionIndex}
                  onClick={() => addTag(suggestion)}
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm',
                    'hover:bg-gray-100',
                    'focus:outline-none',
                    index === focusedSuggestionIndex && 'bg-gray-100'
                  )}
                >
                  {suggestion}
                </button>
              ))
            )}
          </div>
        )}

        {/* Screen reader announcements */}
        <div className="sr-only" role="status" aria-live="polite">
          {value.length} etiquetas seleccionadas
        </div>
      </div>
    );
  }
);

TagInput.displayName = 'TagInput';
